import Draggable, { DraggableEvent } from "react-draggable";
import { useRef, useState, useEffect } from "react";
import { Box, Button, DialogContent, IconButton, Theme, Typography, makeStyles, useTheme } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import Loading from "../Loading/Loading";
import MoreVert from "../Icons/MoreVert";
import LyfeTooltip from "../LyfeTooltip/LyfeTooltip";

interface INonModal {
	open: boolean;
	handleClose: () => void;
	children: any;
	titleKey: string;
	confirmButton: {
		loading?: boolean;
		disabled: boolean;
		isGrossAmountExceeded: boolean;
		onConfirm: () => void;
	};
	cancelButton: {
		disabled: boolean;
	};
	tertiaryButton: {
		disabled: boolean;
		areInconsistentAmounts: boolean;
		onClick: () => void;
	};
	infoPermanent: string;
	infoMessage?: string;
	position: { x: number; y: number };
	setPosition: (position: { x: number; y: number }) => void;
}

const NonModal = (props: INonModal) => {
	const { t } = useTranslation();
	const [isDialogFocused, setIsDialogFocused] = useState(false);
	const modalRef = useRef<HTMLDivElement>(null);
	const { palette } = useTheme();
	const { open, handleClose } = props;
	const { modal, draggableWindowTitleStyle, actions, dialogContent, dialogContent_focused, infoMessage } = classes();

	useEffect(() => {
		if (!open) return;
		window.onbeforeunload = () => true;
		document.addEventListener("click", checkAllClick);
		return () => {
			window.onbeforeunload = null;
			document.removeEventListener("click", checkAllClick);
		};
	}, [open]);

	const checkAllClick = (event: MouseEvent) => {
		if (modalRef.current?.contains(event.target as Node)) {
			setIsDialogFocused(true);
		} else {
			setIsDialogFocused(false);
		}
	};

	const handleDrag = (e: DraggableEvent, data: { x: number; y: number }) => {
		props.setPosition &&
			props.setPosition({
				...props.position,
				x: data.x,
				y: data.y,
			});
	};

	return (
		<Draggable
			bounds="body"
			onStop={handleDrag}
			position={props.position || { x: 0, y: 0 }}
			handle="#draggable-dialog-title"
			cancel={'[class*="MuiDialogContent-root"]'}
		>
			<div style={{ borderColor: isDialogFocused ? palette.tableDivider.main : "transparent" }} className={modal} ref={modalRef}>
				<div id="draggable-dialog-title" className={draggableWindowTitleStyle}>
					<Box display={"flex"} alignItems={"center"}>
						<Box mr={1} display={"flex"}>
							<MoreVert />
						</Box>
						{t(props.titleKey)}
						<IconButton onClick={handleClose} style={{ marginLeft: "auto" }}>
							<Close />
						</IconButton>
					</Box>
				</div>
				<DialogContent dividers className={isDialogFocused ? dialogContent_focused : dialogContent}>
					<Box mb={1}>
						<Typography variant="body2" className={infoMessage}>
							{t(props.infoPermanent)}
						</Typography>
					</Box>
					{props.infoMessage && (
						<Box mb={2}>
							<Typography variant="body2" className={infoMessage}>
								{t(props.infoMessage)}
							</Typography>
						</Box>
					)}
					{props.children}
				</DialogContent>
				<div className={actions}>
					<LyfeTooltip
						title={props.tertiaryButton.areInconsistentAmounts ? t("transaction-adjustment-revert-multiple-non-identical") : ""}
					>
						<div style={{ marginRight: "auto" }}>
							<Button
								onClick={props.tertiaryButton.onClick}
								color="primary"
								style={{ padding: "7px" }}
								disabled={props.tertiaryButton.disabled}
							>
								{t("transaction-adjustment-revert")}
							</Button>
						</div>
					</LyfeTooltip>
					<Button onClick={handleClose} color="primary" variant="outlined" disabled={props.cancelButton.disabled}>
						{t("cancel")}
					</Button>
					<LyfeTooltip
						title={props.confirmButton.isGrossAmountExceeded ? t("transaction-adjustment-confirm-multiple-exceeded") : ""}
					>
						<div>
							<Button
								color="primary"
								variant="contained"
								type="submit"
								style={{ marginLeft: "12px" }}
								onClick={props.confirmButton.onConfirm}
								disabled={props.confirmButton.disabled}
								startIcon={props.confirmButton.loading && <Loading size="14px" color="secondary" />}
							>
								{t("confirm")}
							</Button>
						</div>
					</LyfeTooltip>
				</div>
			</div>
		</Draggable>
	);
};

export default NonModal;

const classes = makeStyles<Theme>((theme: Theme) => {
	const { palette } = theme;
	return {
		draggableWindowTitleStyle: {
			fontSize: 18,
			backgroundColor: palette.inputTableDivider.main,
			padding: "4px 24px",
		},
		dialogContent: {
			borderColor: palette.tableDivider.main,
		},
		dialogContent_focused: {
			borderTop: `1px solid ${palette.blue.main}`,
		},
		modal: {
			position: "fixed",
			top: "30%",
			left: "30%",
			transform: "translate(-50%, -50%)",
			width: "500px",
			backgroundColor: "#fff",
			zIndex: 1202,
			boxShadow: "0 6px 10px 0 rgba(0, 0, 0, 0.08)",
			border: "1px solid transparent",
			borderRadius: 4,
		},
		actions: {
			display: "flex",
			padding: "12px 24px",
		},
		infoMessage: {
			color: palette.tableText.main,
		},
	};
});
